/**
 * ZIP export pour la plateforme Tej.
 *
 * Tej attend un fichier .zip contenant le XML de déclaration.
 * Pas de dépendance externe (pas de jszip dans le projet) :
 * implémentation minimale du format ZIP (Local file headers + EOCD)
 * en pur TypeScript / Uint8Array.
 *
 * Limites intentionnelles (suffisantes pour des XML < 64 MiB) :
 * - Pas de compression (stored / method 0) — Tej accepte le stockage non compressé
 * - Un seul fichier par archive
 * - CRC-32 calculé en pur JS
 */

// ─── API publique ─────────────────────────────────────────────────────────────

/**
 * Emballe un string XML dans un fichier .zip.
 * @param xml       Contenu XML (string UTF-8)
 * @param filename  Nom du fichier XML à l'intérieur du zip (ex "DeclarationsRS_7654321_2026_01.xml")
 * @returns         Blob ZIP téléchargeable
 */
export function packageTejZip(xml: string, filename: string): Blob {
  const encoder = new TextEncoder();
  const fileData = encoder.encode(xml);
  const filenameBytes = encoder.encode(filename);

  const crc = crc32(fileData);
  const dosDate = toDosDateTime(new Date());

  // Local file header (30 bytes + filename)
  const localHeader = buildLocalFileHeader(filenameBytes, fileData, crc, dosDate);

  // Central directory entry
  const centralEntry = buildCentralDirEntry(filenameBytes, fileData, crc, dosDate, localHeader.byteLength);

  // End of central directory record
  const eocd = buildEocd(centralEntry.byteLength, localHeader.byteLength + fileData.byteLength);

  // Concaténer
  const total = localHeader.byteLength + fileData.byteLength + centralEntry.byteLength + eocd.byteLength;
  const out = new Uint8Array(total);
  let offset = 0;
  out.set(localHeader, offset); offset += localHeader.byteLength;
  out.set(fileData,    offset); offset += fileData.byteLength;
  out.set(centralEntry, offset); offset += centralEntry.byteLength;
  out.set(eocd,        offset);

  return new Blob([out], { type: 'application/zip' });
}

// ─── Helpers ZIP ─────────────────────────────────────────────────────────────

function buildLocalFileHeader(
  filenameBytes: Uint8Array,
  fileData: Uint8Array,
  crc: number,
  dosDate: { date: number; time: number },
): Uint8Array {
  const buf = new ArrayBuffer(30 + filenameBytes.byteLength);
  const view = new DataView(buf);
  let o = 0;

  view.setUint32(o, 0x04034b50, true); o += 4;  // Local file header signature
  view.setUint16(o, 20, true);          o += 2;  // Version needed: 2.0
  view.setUint16(o, 0, true);           o += 2;  // General purpose bit flag
  view.setUint16(o, 0, true);           o += 2;  // Compression method: stored
  view.setUint16(o, dosDate.time, true); o += 2; // Last mod time
  view.setUint16(o, dosDate.date, true); o += 2; // Last mod date
  view.setUint32(o, crc, true);          o += 4; // CRC-32
  view.setUint32(o, fileData.byteLength, true); o += 4; // Compressed size
  view.setUint32(o, fileData.byteLength, true); o += 4; // Uncompressed size
  view.setUint16(o, filenameBytes.byteLength, true); o += 2; // Filename length
  view.setUint16(o, 0, true);           o += 2;  // Extra field length

  const arr = new Uint8Array(buf);
  arr.set(filenameBytes, 30);
  return arr;
}

function buildCentralDirEntry(
  filenameBytes: Uint8Array,
  fileData: Uint8Array,
  crc: number,
  dosDate: { date: number; time: number },
  localHeaderOffset: number,
): Uint8Array {
  const buf = new ArrayBuffer(46 + filenameBytes.byteLength);
  const view = new DataView(buf);
  let o = 0;

  view.setUint32(o, 0x02014b50, true); o += 4;  // Central dir signature
  view.setUint16(o, 20, true);          o += 2;  // Version made by
  view.setUint16(o, 20, true);          o += 2;  // Version needed
  view.setUint16(o, 0, true);           o += 2;  // Bit flag
  view.setUint16(o, 0, true);           o += 2;  // Compression: stored
  view.setUint16(o, dosDate.time, true); o += 2;
  view.setUint16(o, dosDate.date, true); o += 2;
  view.setUint32(o, crc, true);          o += 4;
  view.setUint32(o, fileData.byteLength, true); o += 4; // Compressed
  view.setUint32(o, fileData.byteLength, true); o += 4; // Uncompressed
  view.setUint16(o, filenameBytes.byteLength, true); o += 2;
  view.setUint16(o, 0, true);           o += 2;  // Extra length
  view.setUint16(o, 0, true);           o += 2;  // Comment length
  view.setUint16(o, 0, true);           o += 2;  // Disk number start
  view.setUint16(o, 0, true);           o += 2;  // Internal attributes
  view.setUint32(o, 0, true);           o += 4;  // External attributes
  view.setUint32(o, localHeaderOffset, true); o += 4; // Relative offset of local header

  const arr = new Uint8Array(buf);
  arr.set(filenameBytes, 46);
  return arr;
}

function buildEocd(centralDirSize: number, centralDirOffset: number): Uint8Array {
  const buf = new ArrayBuffer(22);
  const view = new DataView(buf);
  let o = 0;

  view.setUint32(o, 0x06054b50, true); o += 4;  // EOCD signature
  view.setUint16(o, 0, true);           o += 2;  // Disk number
  view.setUint16(o, 0, true);           o += 2;  // Disk with central dir
  view.setUint16(o, 1, true);           o += 2;  // Number of entries on disk
  view.setUint16(o, 1, true);           o += 2;  // Total entries
  view.setUint32(o, centralDirSize, true); o += 4;
  view.setUint32(o, centralDirOffset, true); o += 4;
  view.setUint16(o, 0, true);           // Comment length

  return new Uint8Array(buf);
}

function toDosDateTime(d: Date): { date: number; time: number } {
  const time =
    ((d.getHours() & 0x1f) << 11) |
    ((d.getMinutes() & 0x3f) << 5) |
    ((d.getSeconds() >> 1) & 0x1f);
  const date =
    (((d.getFullYear() - 1980) & 0x7f) << 9) |
    (((d.getMonth() + 1) & 0x0f) << 5) |
    (d.getDate() & 0x1f);
  return { date, time };
}

// ─── CRC-32 (IEEE 802.3) ─────────────────────────────────────────────────────

const CRC_TABLE: Uint32Array = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    t[i] = c;
  }
  return t;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = (CRC_TABLE[(crc ^ (data[i] ?? 0)) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
